// material-ui
import { Theme } from '@mui/material/styles';

// types
import { OodColors, OodColorVariant } from '../types/colors';
import { ThemeMode } from '../types/config';
import { getNightOrDayPalette } from './getNightOrDayPalette';

// ==============================|| CUSTOM FUNCTION - COLORS ||============================== //

const OodColorPalette: Record<OodColors, Record<ThemeMode, OodColorVariant>> = {
	blue: {
		[ThemeMode.LIGHT]: { background: '#F0F5FF', border: '#D6E4FF', content: '#1890FF' },
		[ThemeMode.DARK]: { background: '#051229', border: '#071C40', content: '#1890FF' },
	},
	purple: {
		[ThemeMode.LIGHT]: { background: '#F9F0FF', border: '#EFDBFF', content: '#722ED1' },
		[ThemeMode.DARK]: { background: '#190420', border: '#2C0637', content: '#722ED1' },
	},
	red: {
		[ThemeMode.LIGHT]: { background: '#FFF1F0', border: '#FFCCC7', content: '#F5222D' },
		[ThemeMode.DARK]: { background: '#200A04', border: '#321106', content: '#F5222D' },
	},
	orange: {
		[ThemeMode.LIGHT]: { background: '#FFF7E6', border: '#FFE7BA', content: '#FA8C16' },
		[ThemeMode.DARK]: { background: '#1C1203', border: '#2E2005', content: '#FA8C16' },
	},
	yellow: {
		[ThemeMode.LIGHT]: { background: '#FEFFE6', border: '#FFF566', content: '#D4B106' },
		[ThemeMode.DARK]: { background: '#1F1A00', border: '#2E2700', content: '#D4B106' },
	},
	green: {
		[ThemeMode.LIGHT]: { background: '#F6FFED', border: '#D9F7BE', content: '#52C41A' },
		[ThemeMode.DARK]: { background: '#0D1A00', border: '#162905', content: '#52C41A' },
	},
	darkGreen: {
		[ThemeMode.LIGHT]: { background: '#D9F7BE', border: '#95DE64', content: '#389e0d' },
		[ThemeMode.DARK]: { background: '#001800', border: '#002800', content: '#389e0d' },
	},
	cyan: {
		[ThemeMode.LIGHT]: { background: '#E6FFFB', border: '#B5F5EC', content: '#13C2C2' },
		[ThemeMode.DARK]: { background: '#001A15', border: '#052824', content: '#13C2C2' },
	},
	grey: {
		[ThemeMode.LIGHT]: { background: '#FAFAFA', border: '#D9D9D9', content: '#595959' },
		[ThemeMode.DARK]: { background: '#141414', border: '#1F1F1F', content: '#595959' },
	},
	pink: {
		[ThemeMode.LIGHT]: { background: '#FFE4F7', border: '#E7A2BE', content: '#EB2F96' },
		[ThemeMode.DARK]: { background: '#890046', border: '#FF52B1', content: '#EB2F96' },
	},
} as const;

const isPaletteColor = (color: string): color is keyof typeof OodColorPalette => {
	return color in OodColorPalette;
};

const getOodColor = (theme: Theme, color?: OodColors | string | null): OodColorVariant => {
	if (color && isPaletteColor(color)) {
		return OodColorPalette[color][theme.palette.mode];
	}

	return {
		background: theme.palette[getNightOrDayPalette(theme)][0],
		border: theme.palette[getNightOrDayPalette(theme)][100],
		content: theme.palette[getNightOrDayPalette(theme)][400],
	};
};

export const determineColor = (theme: Theme, value: number): string | undefined => {
	if (value >= 0 && value < 20) {
		return theme.palette.dustred[400];
	} else if (value >= 20 && value < 40) {
		return theme.palette.orange[400];
	} else if (value >= 40 && value < 60) {
		return theme.palette.grey[400];
	} else if (value >= 60 && value < 80) {
		return theme.palette.green[400];
	} else if (value >= 80) {
		return theme.palette.green[500];
	}
};

export default getOodColor;
